/**
 * Combat Manager
 * 
 * Handles all combat-related logic including targeting, cannon focus,
 * range visualization, and combat socket handlers.
 */

import Phaser from 'phaser';
import { Socket } from 'socket.io-client';
import { TILE_SIZE } from '../types';
import { CombatSystem, CannonEffectData } from '../systems/CombatSystem';
import { ShipSprite } from '../systems/ShipRenderer';
import { drawCombatRange } from '../utils/RadiusRenderer';

export interface CombatManagerCallbacks {
  getSocket: () => Socket | null;
  getLocalShip: () => ShipSprite | null;
  getShips: () => Map<string, ShipSprite>;
  getNPCs: () => Map<string, Phaser.GameObjects.Container>;
  getLocalPlayerId: () => string;
  isPlayerDead: () => boolean;
  isBoardingActive: () => boolean;
  hideTargetInfo: () => void;
  clearAllPathVisualizations: () => void;
  clearPathVisualization: (isCombat?: boolean) => void;
  isInSafeZone: (x: number, y: number) => boolean;
}

export class CombatManager {
  private scene: Phaser.Scene;
  private callbacks: CombatManagerCallbacks;
  private combatSystem: CombatSystem;
  
  // Combat state
  private selectedTarget: string | null = null;
  private cannonFocusMultiplier: number = 1.0;
  
  // Combat visuals
  private combatTrackingLine: Phaser.GameObjects.Graphics | null = null;
  private hoverRangeGraphics: Phaser.GameObjects.Graphics | null = null;
  private targetInfo: Phaser.GameObjects.Container | null = null;
  
  // Performance optimization: throttle updates
  private lastTrackingLineUpdate = 0;
  private lastTargetInfoUpdate = 0;
  private updateThrottleMs = 33; // Update ~30 times per second instead of 60

   constructor(scene: Phaser.Scene, callbacks: CombatManagerCallbacks) {
     this.scene = scene;
     this.callbacks = callbacks;
     
     this.combatSystem = new CombatSystem(scene, {
       getShips: callbacks.getShips,
       getLocalShip: callbacks.getLocalShip,
       hideTargetInfo: callbacks.hideTargetInfo,
       clearAllPathVisualizations: callbacks.clearAllPathVisualizations,
       isInSafeZone: () => {
         const localShip = callbacks.getLocalShip();
         if (!localShip) {
           return false;
         }

         const securityTier = localShip.shipData?.securityTier as
           | 'safe_zone'
           | 'contested'
           | 'open_waters'
           | undefined;

         if (securityTier === 'safe_zone') {
           return true;
         }

         return callbacks.isInSafeZone(localShip.x, localShip.y);
       },
     });
   }

  // ============ GETTERS/SETTERS ============

  getSelectedTarget(): string | null {
    return this.selectedTarget;
  }

  setSelectedTarget(target: string | null): void {
    this.selectedTarget = target;
  }

  getCombatSystem(): CombatSystem {
    return this.combatSystem;
  }

  getCannonFocusMultiplier(): number {
    return this.cannonFocusMultiplier;
  }

  setCannonFocusMultiplier(multiplier: number): void {
    this.cannonFocusMultiplier = multiplier;
  }

  // ============ TARGET ENGAGEMENT ============

  /**
   * Engage a target - tells server to pursue and auto-fire when in range
   */
  engageTarget(shipId: string): void {
    const ships = this.callbacks.getShips();
    
    // Deselect previous target visually
    if (this.selectedTarget) {
      const prevShip = ships.get(this.selectedTarget);
      if (prevShip) {
        // Handle both shape hulls (circles/squares) and container hulls (ship visuals)
        if ('setStrokeStyle' in prevShip.hull) {
          prevShip.hull.setStrokeStyle(2, 0xffffff);
        } else if ('getAt' in prevShip.hull) {
          // For container hulls, apply stroke to the first child (main hull shape)
          const container = prevShip.hull as any;
          const mainHull = container.getAt(0);
          if (mainHull && 'setStrokeStyle' in mainHull) {
            mainHull.setStrokeStyle(2, 0xffffff);
          }
        }
      }
    }
    
    this.selectedTarget = shipId;
    
    // Notify cannon focus UI that we have a target
    const targetShip = ships.get(shipId);
    if (targetShip) {
      this.scene.events.emit('cannonFocus:targetUpdate', {
        name: targetShip.shipData.name,
        hp: targetShip.shipData.hullHp,
        maxHp: targetShip.shipData.maxHullHp,
        faction: targetShip.shipData.faction || 'neutral',
        level: targetShip.shipData.level || 1,
      });
    } else {
      this.scene.events.emit('cannonFocus:targetUpdate', {
        name: null,
        hp: 0,
        maxHp: 0,
        faction: 'neutral',
        level: 1,
      });
    }
    
    // Clear ALL path visualizations - combat uses tracking line instead
    this.callbacks.clearAllPathVisualizations();
    
    // Highlight new target
    const ship = ships.get(shipId);
    if (ship) {
      // Handle both shape hulls (circles/squares) and container hulls (ship visuals)
      if ('setStrokeStyle' in ship.hull) {
        ship.hull.setStrokeStyle(3, 0xff0000);
      } else if ('getAt' in ship.hull) {
        // For container hulls, apply stroke to the first child (main hull shape)
        const container = ship.hull as any;
        const mainHull = container.getAt(0);
        if (mainHull && 'setStrokeStyle' in mainHull) {
          mainHull.setStrokeStyle(3, 0xff0000);
        }
      }
    }
    
    // Tell server to engage (pursue + auto-fire)
    const socket = this.callbacks.getSocket();
    console.log(`⚔️ Emitting shipEngage to server, targetId: ${shipId}`);
    socket?.emit('shipEngage', { targetId: shipId });
  }

  /**
   * Disengage current target - stop pursuing and firing
   */
  disengageTarget(): void {
    const ships = this.callbacks.getShips();
    
    if (this.selectedTarget) {
      const prevShip = ships.get(this.selectedTarget);
      if (prevShip) {
        // Handle both shape hulls (circles/squares) and container hulls (ship visuals)
        if ('setStrokeStyle' in prevShip.hull) {
          prevShip.hull.setStrokeStyle(2, 0xffffff);
        } else if ('getAt' in prevShip.hull) {
          // For container hulls, apply stroke to the first child (main hull shape)
          const container = prevShip.hull as any;
          const mainHull = container.getAt(0);
          if (mainHull && 'setStrokeStyle' in mainHull) {
            mainHull.setStrokeStyle(2, 0xffffff);
          }
        }
      }
    }
    
    this.selectedTarget = null;
    this.callbacks.hideTargetInfo();
    
    // Notify cannon focus UI that we no longer have a target
    this.scene.events.emit('cannonFocus:targetUpdate', {
      name: null,
      hp: 0,
      maxHp: 0,
      faction: 'neutral',
      level: 1,
    });
    
    // Deactivate cannon focus if active
    this.scene.events.emit('cannonFocus:deactivate');
    
    // Clear combat tracking line
    if (this.combatTrackingLine) {
      this.combatTrackingLine.clear();
    }
    
    // Tell server to disengage
    const socket = this.callbacks.getSocket();
    socket?.emit('shipDisengage');
  }

  // ============ CANNON FOCUS ============

  /**
   * Check if cannon focus is available
   */
  isCannonFocusAvailable(): boolean {
    if (!this.selectedTarget) return false;
    
    const localShip = this.callbacks.getLocalShip();
    if (!localShip) return false;

    const ships = this.callbacks.getShips();
    const targetShip = ships.get(this.selectedTarget);
    if (!targetShip) return false;

    // Focus mode should not be available in safe zones
    if (this.isShipInSafeZone(localShip) || this.isShipInSafeZone(targetShip)) {
      return false;
    }

    if (this.callbacks.isPlayerDead()) return false;
    if (this.callbacks.isBoardingActive()) return false;

    return true;
  }

  /**
   * Toggle cannon focus mode
   */
  toggleCannonFocus(): void {
    if (!this.isCannonFocusAvailable()) {
      if (!this.selectedTarget) {
        console.log('⚔️ Cannot activate cannon focus - no target engaged');
      } else {
        console.log('⚔️ Cannot activate cannon focus right now');
      }
      return;
    }
    
    // Emit event to React component to toggle cannon focus
    this.scene.events.emit('cannonFocus:activate');
    console.log('🎯 Cannon Focus activated - hold WASD to aim, ESC to close');
  }

  /**
   * Sync cannon focus eligibility with real combat state
   * NOTE: This only prevents NEW activations, does not deactivate active minigame
   * The minigame should only be deactivated by explicit user action or combat end
   */
  syncCannonFocusEligibility(): void {
    const hasEligibleTarget = this.isCannonFocusAvailable();

    // Only force disengage if target becomes completely invalid
    if (!hasEligibleTarget && this.selectedTarget) {
      const ships = this.callbacks.getShips();
      const targetShip = ships.get(this.selectedTarget);
      const localShip = this.callbacks.getLocalShip();
      
      const shouldForceDisengage = !targetShip
        || (localShip ? this.isShipInSafeZone(localShip) : false)
        || (targetShip ? this.isShipInSafeZone(targetShip) : false);

      if (shouldForceDisengage) {
        this.disengageTarget();
      }
    }
  }

  /**
   * Check if a ship is in a safe zone
   */
  private isShipInSafeZone(ship: ShipSprite): boolean {
    const shipData = ship.shipData;
    if (!shipData) return false;

    const securityTier = shipData.securityTier as
      | 'safe_zone'
      | 'contested'
      | 'open_waters'
      | undefined;

    if (securityTier === 'safe_zone') {
      return true;
    }

    return this.callbacks.isInSafeZone(shipData.x, shipData.y);
  }

  // ============ COMBAT VISUALS ============

  /**
   * Update the combat tracking line - direct line from player to target
   * OPTIMIZED: Throttled to reduce update frequency
   */
  updateCombatTrackingLine(): void {
    const now = Date.now();
    // Throttle updates to reduce CPU usage
    if (now - this.lastTrackingLineUpdate < this.updateThrottleMs) {
      return;
    }
    this.lastTrackingLineUpdate = now;
    
    if (this.combatTrackingLine) {
      this.combatTrackingLine.clear();
    }
    
    const localShip = this.callbacks.getLocalShip();
    if (!this.selectedTarget || !localShip) {
      return;
    }
    
    const ships = this.callbacks.getShips();
    const target = ships.get(this.selectedTarget);
    if (!target) {
      return;
    }
    
    if (!this.combatTrackingLine) {
      this.combatTrackingLine = this.scene.add.graphics();
      this.combatTrackingLine.setDepth(55);
    }
    
    // Calculate distance
    const dist = Phaser.Math.Distance.Between(
      localShip.x, localShip.y,
      target.x, target.y
    );
    const distTiles = Math.round(dist / TILE_SIZE);
    const shipData = localShip.shipData;
    const rangeTiles = shipData.cannonRange ? Math.ceil(shipData.cannonRange / TILE_SIZE) : 8;
    const inRange = distTiles <= rangeTiles;
    
    // Draw grid-based combat range indicator
    drawCombatRange(this.combatTrackingLine, localShip.x, localShip.y, rangeTiles, inRange);
    
    // Draw line - red if in range, orange if chasing
    const lineColor = inRange ? 0xff0000 : 0xff6600;
    this.combatTrackingLine.lineStyle(3, lineColor, 0.8);
    this.combatTrackingLine.moveTo(localShip.x, localShip.y);
    this.combatTrackingLine.lineTo(target.x, target.y);
    this.combatTrackingLine.strokePath();
    
    // Draw target reticle (corner brackets)
    this.combatTrackingLine.lineStyle(2, lineColor, 0.6);
    const reticleSize = TILE_SIZE * 1.5;
    const corners = [
      { x: target.x - reticleSize/2, y: target.y - reticleSize/2, dx: 6, dy: 0, dx2: 0, dy2: 6 },
      { x: target.x + reticleSize/2, y: target.y - reticleSize/2, dx: -6, dy: 0, dx2: 0, dy2: 6 },
      { x: target.x - reticleSize/2, y: target.y + reticleSize/2, dx: 6, dy: 0, dx2: 0, dy2: -6 },
      { x: target.x + reticleSize/2, y: target.y + reticleSize/2, dx: -6, dy: 0, dx2: 0, dy2: -6 },
    ];
    for (const c of corners) {
      this.combatTrackingLine.moveTo(c.x, c.y);
      this.combatTrackingLine.lineTo(c.x + c.dx, c.y + c.dy);
      this.combatTrackingLine.moveTo(c.x, c.y);
      this.combatTrackingLine.lineTo(c.x + c.dx2, c.y + c.dy2);
    }
    this.combatTrackingLine.strokePath();
  }

  /**
   * Clear combat tracking line
   */
  clearCombatTrackingLine(): void {
    if (this.combatTrackingLine) {
      this.combatTrackingLine.clear();
    }
  }

  /**
   * Show hover range for a ship
   */
  showHoverRange(ship: ShipSprite): void {
    if (!this.hoverRangeGraphics) {
      this.hoverRangeGraphics = this.scene.add.graphics();
      this.hoverRangeGraphics.setDepth(50);
    }
    
    this.hoverRangeGraphics.clear();
    
    const shipData = ship.shipData;
    const rangeTiles = shipData.cannonRange ? Math.ceil(shipData.cannonRange / TILE_SIZE) : 8;
    
    drawCombatRange(this.hoverRangeGraphics, ship.x, ship.y, rangeTiles, false);
  }

  /**
   * Hide hover range
   */
  hideHoverRange(): void {
    if (this.hoverRangeGraphics) {
      this.hoverRangeGraphics.clear();
    }
  }

  // ============ COMBAT EFFECTS ============

  /**
   * Show cannon fire effect
   */
  showCannonEffect(data: CannonEffectData): void {
    this.combatSystem.showCannonEffect(data);
  }

  /**
   * Show ship sinking effect
   */
  showShipSinkingEffect(ship: ShipSprite, shipName: string, pieces: number): void {
    this.combatSystem.showShipSinkingEffect(ship, shipName, pieces);
  }

  /**
   * Show loot gained effect
   */
  showLootGainedEffect(pieces: number, bounty: number, xp: number = 0, newLevel?: number): void {
    this.combatSystem.showLootGainedEffect(pieces, bounty, xp, newLevel);
  }


  /**
   * Render escape smoke effect
   */
  renderEscapeSmokeEffect(x: number, y: number): void {
    const graphics = this.scene.add.graphics();
    graphics.setDepth(100);
    
    // Create multiple smoke puffs
    for (let i = 0; i < 8; i++) {
      const offsetX = (Math.random() - 0.5) * TILE_SIZE * 2;
      const offsetY = (Math.random() - 0.5) * TILE_SIZE * 2;
      const size = TILE_SIZE * (0.5 + Math.random() * 0.5);
      
      const puff = this.scene.add.graphics();
      puff.setDepth(100);
      puff.fillStyle(0x888888, 0.8);
      puff.fillCircle(x + offsetX, y + offsetY, size);
      
      (this.scene as any).tweens.add({
        targets: puff,
        alpha: 0,
        scaleX: 2,
        scaleY: 2,
        duration: 1000 + Math.random() * 500,
        onComplete: () => puff.destroy(),
      });
    }
  }

  /**
   * Show signature move visual effect
   */
  showSignatureMoveVisual(x: number, y: number, color: string, aoeRadius: number, element: string): void {
    const colorNum = Phaser.Display.Color.HexStringToColor(color).color;
    
    // Create expanding ring
    const ring = this.scene.add.graphics();
    ring.setDepth(90);
    ring.lineStyle(4, colorNum, 1);
    ring.strokeCircle(x, y, 10);
    
    (this.scene as any).tweens.add({
      targets: ring,
      scaleX: aoeRadius / 10,
      scaleY: aoeRadius / 10,
      alpha: 0,
      duration: 500,
      onComplete: () => ring.destroy(),
    });
    
    // Create element-specific particles
    const particleCount = 12;
    for (let i = 0; i < particleCount; i++) {
      const angle = (i / particleCount) * Math.PI * 2;
      const distance = aoeRadius * 0.8;
      const endX = x + Math.cos(angle) * distance;
      const endY = y + Math.sin(angle) * distance;
      
      const particle = this.scene.add.graphics();
      particle.setDepth(91);
      particle.fillStyle(colorNum, 1);
      particle.fillRect(-4, -4, 8, 8);
      particle.setPosition(x, y);
      
      (this.scene as any).tweens.add({
        targets: particle,
        x: endX,
        y: endY,
        alpha: 0,
        duration: 400,
        delay: i * 30,
        onComplete: () => particle.destroy(),
      });
    }
  }

  // ============ TARGET INFO ============

  /**
   * Show target info panel
   */
  showTargetInfo(target: ShipSprite): void {
    this.hideTargetInfo();
    
    const { width } = this.scene.cameras.main;
    
    this.targetInfo = this.scene.add.container(width - 120, 100);
    this.targetInfo.setScrollFactor(0);
    this.targetInfo.setDepth(100);
    
    const bg = this.scene.add.rectangle(0, 0, 200, 80, 0x000000, 0.8);
    bg.setStrokeStyle(2, 0xff0000);
    this.targetInfo.add(bg);
    
    const nameText = this.scene.add.text(0, -25, target.shipData.name, {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: '12px',
      color: '#ff6666',
      fontStyle: 'bold',
    });
    nameText.setOrigin(0.5);
    this.targetInfo.add(nameText);
    
    const hpPercent = Math.round((target.shipData.hullHp / target.shipData.maxHullHp) * 100);
    const hpText = this.scene.add.text(0, 0, `HP: ${target.shipData.hullHp}/${target.shipData.maxHullHp} (${hpPercent}%)`, {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: '10px',
      color: '#ffffff',
    });
    hpText.setOrigin(0.5);
    this.targetInfo.add(hpText);
    
    const levelText = this.scene.add.text(0, 20, `Level ${target.shipData.level || 1}`, {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: '10px',
      color: '#aaaaaa',
    });
    levelText.setOrigin(0.5);
    this.targetInfo.add(levelText);
  }

  /**
   * Hide target info panel
   */
  hideTargetInfo(): void {
    if (this.targetInfo) {
      this.targetInfo.destroy();
      this.targetInfo = null;
    }
  }

  /**
   * Update target info with current target data
   * OPTIMIZED: Throttled to reduce update frequency
   */
  updateTargetInfo(): void {
    if (!this.selectedTarget) return;
    
    const now = Date.now();
    // Throttle updates to reduce CPU usage
    if (now - this.lastTargetInfoUpdate < this.updateThrottleMs) {
      return;
    }
    this.lastTargetInfoUpdate = now;
    
    const ships = this.callbacks.getShips();
    const targetShip = ships.get(this.selectedTarget);
    
    if (targetShip) {
      this.scene.events.emit('cannonFocus:targetUpdate', {
        name: targetShip.shipData.name,
        hp: targetShip.shipData.hullHp,
        maxHp: targetShip.shipData.maxHullHp,
        faction: targetShip.shipData.faction || 'neutral',
        level: targetShip.shipData.level || 1,
      });
    } else {
      this.scene.events.emit('cannonFocus:targetUpdate', {
        name: null,
        hp: 0,
        maxHp: 0,
        faction: 'neutral',
        level: 1,
      });
    }
  }
}
